import { call, put, takeLatest } from "redux-saga/effects";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getJewelleryRequest,
  getJewellerySuccess,
  getJewelleryFailure,
  getJewelleryOrdersRequest,
  getJewelleryOrdersSuccess,
  getJewelleryOrdersFailure,
  createJewelleryOrderRequest,
  createJewelleryOrderSuccess,
  createJewelleryOrderFailure,
  createJewelleryOfflineOrderRequest,
  createJewelleryOfflineOrderSuccess,
  createJewelleryOfflineOrderFailure,
  verifyJewelleryOfflineOrderRequest,
  verifyJewelleryOfflineOrderSuccess,
  verifyJewelleryOfflineOrderFailure,
} from "./jewellerySlice";
import {
  fetchJewelleryCartRequest,
  fetchJewelleryCartSuccess,
  fetchJewelleryCartFailure,
  addJewelleryToCartRequest,
  addJewelleryToCartFailure,
  removeJewelleryFromCartRequest,
  removeJewelleryFromCartFailure,
  checkoutJewelleryCartRequest,
  checkoutJewelleryCartSuccess,
  checkoutJewelleryCartFailure,
} from "./jewelleryCartSlice";
import { mapServerCartItems } from "../../Components/Jewellery/jewelleryUtils";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

function* fetchJewellery() {
  try {
    const response = yield call(() =>
      axios.get(
        `${baseUrl}/api/v1/artificial-jewellery/getArtificialJewelleryMobile`,
      ),
    );

    if (response.data?.success) {
      const list = Array.isArray(response.data.data) ? response.data.data : [];
      yield put(getJewellerySuccess(list));
    } else {
      const message =
        response.data?.message || "Failed to fetch artificial jewellery";
      yield put(getJewelleryFailure(message));
      toast.error(message);
    }
  } catch (error) {
    yield put(getJewelleryFailure(error.message));
    toast.error("Error fetching artificial jewellery");
  }
}

function* fetchJewelleryOrders(action) {
  try {
    const page = action.payload?.page || 1;
    const limit = action.payload?.limit || 100;
    const response = yield call(() =>
      axios.get(
        `${baseUrl}/api/v1/artificial-jewellery/getArtificialJewelleryOrdersMobile`,
        { params: { page, limit } },
      ),
    );

    if (response.data?.success) {
      yield put(
        getJewelleryOrdersSuccess(
          response.data.data || { data: [], total: 0, page, limit },
        ),
      );
    } else {
      const message =
        response.data?.message || "Failed to fetch artificial jewellery orders";
      yield put(getJewelleryOrdersFailure(message));
      toast.error(message);
    }
  } catch (error) {
    yield put(getJewelleryOrdersFailure(error.message));
    toast.error("Error fetching artificial jewellery orders");
  }
}

function* createJewelleryOrder(action) {
  try {
    const response = yield call(() =>
      axios.post(
        `${baseUrl}/api/v1/artificial-jewellery/createArtificialJewelleryOrder`,
        action.payload,
      ),
    );

    if (response.data?.success) {
      yield put(createJewelleryOrderSuccess(response.data));
      const session = response.data?.data;
      const opensGateway = Boolean(
        session?.token || session?.url || session?.encVal || session?.checkout_url,
      );
      if (!opensGateway) {
        toast.success(response.data.message || "Artificial jewellery ordered");
      }
    } else {
      const message = response.data?.message || "Could not place the order";
      yield put(createJewelleryOrderFailure(message));
      toast.error(message);
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error.message ||
      "Error creating artificial jewellery order";
    yield put(createJewelleryOrderFailure(message));
    toast.error(message);
  }
}

function* createJewelleryOfflineOrder(action) {
  try {
    const response = yield call(() =>
      axios.post(
        `${baseUrl}/api/v1/artificial-jewellery/offlineBuyforJeweller`,
        action.payload,
      ),
    );

    if (response.data?.success) {
      yield put(
        createJewelleryOfflineOrderSuccess({
          orderId: response.data.data,
          order: response.data.order || null,
        }),
      );
      toast.success(response.data.message || "Order created successfully");
    } else {
      const message = response.data?.message || "Could not create the offline order";
      yield put(createJewelleryOfflineOrderFailure(message));
      toast.error(message);
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error.message ||
      "Error creating offline jewellery order";
    yield put(createJewelleryOfflineOrderFailure(message));
    toast.error(message);
  }
}

function* verifyJewelleryOfflineOrder(action) {
  try {
    const response = yield call(() =>
      axios.post(
        `${baseUrl}/api/v1/artificial-jewellery/verifyOfflineOrder`,
        action.payload,
      ),
    );

    if (response.data?.success) {
      yield put(verifyJewelleryOfflineOrderSuccess(response.data));
      toast.success(response.data.message || "Order verified successfully");
    } else {
      const message = response.data?.message || "Could not verify the offline order";
      yield put(verifyJewelleryOfflineOrderFailure(message));
      toast.error(message);
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error.message ||
      "Error verifying offline jewellery order";
    yield put(verifyJewelleryOfflineOrderFailure(message));
    toast.error(message);
  }
}

function* fetchJewelleryCart() {
  try {
    const response = yield call(() =>
      axios.get(`${baseUrl}/api/v1/artificial-jewellery/getCart`),
    );
    if (!response.data?.success) {
      const message = response.data?.message || "Failed to fetch cart";
      yield put(fetchJewelleryCartFailure(message));
      toast.error(message);
      return;
    }
    yield put(fetchJewelleryCartSuccess(mapServerCartItems(response.data.data)));
  } catch (error) {
    const message = error?.response?.data?.message || error.message || "Error fetching cart";
    yield put(fetchJewelleryCartFailure(message));
    toast.error(message);
  }
}

function* addToJewelleryCart(action) {
  try {
    const response = yield call(() =>
      axios.post(`${baseUrl}/api/v1/artificial-jewellery/addToCart`, action.payload),
    );
    if (!response.data?.success) {
      const message = response.data?.message || "Could not add to cart";
      yield put(addJewelleryToCartFailure(message));
      toast.error(message);
      return;
    }
    yield call(fetchJewelleryCart);
    toast.success(response.data.message || "Added to cart");
  } catch (error) {
    const message = error?.response?.data?.message || error.message || "Error adding to cart";
    yield put(addJewelleryToCartFailure(message));
    toast.error(message);
  }
}

function* removeFromJewelleryCart(action) {
  try {
    const response = yield call(() =>
      axios.post(`${baseUrl}/api/v1/artificial-jewellery/removeFromCart`, {
        inventory: action.payload?.inventory,
        ...(Number(action.payload?.quantity) > 0
          ? { quantity: Number(action.payload.quantity) }
          : {}),
      }),
    );
    if (!response.data?.success) {
      const message = response.data?.message || "Could not remove from cart";
      yield put(removeJewelleryFromCartFailure(message));
      toast.error(message);
      return;
    }
    yield call(fetchJewelleryCart);
    toast.success(response.data.message || "Removed from cart");
  } catch (error) {
    const message =
      error?.response?.data?.message || error.message || "Error removing from cart";
    yield put(removeJewelleryFromCartFailure(message));
    toast.error(message);
  }
}

function* checkoutJewelleryCart(action) {
  try {
    const response = yield call(() =>
      axios.post(`${baseUrl}/api/v1/artificial-jewellery/checkoutCart`, action.payload),
    );
    if (!response.data?.success) {
      const message = response.data?.message || "Could not place the order";
      yield put(checkoutJewelleryCartFailure(message));
      toast.error(message);
      yield call(fetchJewelleryCart);
      return;
    }

    if (response.data?.data?.paymentType === "Offline") {
      toast.success(response.data.message || "Order created successfully");
    }

    yield put(checkoutJewelleryCartSuccess(response.data));
    yield call(fetchJewelleryCart);
  } catch (error) {
    const message =
      error?.response?.data?.message || error.message || "Error placing the order";
    yield put(checkoutJewelleryCartFailure(message));
    toast.error(message);
  }
}

export function* jewellerySaga() {
  yield takeLatest(getJewelleryRequest.type, fetchJewellery);
  yield takeLatest(getJewelleryOrdersRequest.type, fetchJewelleryOrders);
  yield takeLatest(createJewelleryOrderRequest.type, createJewelleryOrder);
  yield takeLatest(createJewelleryOfflineOrderRequest.type, createJewelleryOfflineOrder);
  yield takeLatest(verifyJewelleryOfflineOrderRequest.type, verifyJewelleryOfflineOrder);
  yield takeLatest(fetchJewelleryCartRequest.type, fetchJewelleryCart);
  yield takeLatest(addJewelleryToCartRequest.type, addToJewelleryCart);
  yield takeLatest(removeJewelleryFromCartRequest.type, removeFromJewelleryCart);
  yield takeLatest(checkoutJewelleryCartRequest.type, checkoutJewelleryCart);
}
